import { Button } from '@/components/ui-kit/button';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Loader2, SkipForward, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ScreenWithFields, useDynamicForm } from './DynamicFormContext';
import { DynamicFieldType } from './hooks/useDynamicFields';

interface NavigationButtonsProps {
  currentScreen: ScreenWithFields;
  dynamicFieldsLoading: boolean;
  dynamicFieldsCache: Record<string, DynamicFieldType[]>;
  prefetchingStepIndices: Set<number>;
  turnstileToken: string | null;
  turnstileStatus: 'success' | 'error' | 'expired' | 'required';
  onContinue: () => void;
  onSubmit: () => void;
}

export default function NavigationButtons({
  currentScreen,
  dynamicFieldsLoading,
  dynamicFieldsCache,
  prefetchingStepIndices,
  turnstileToken,
  turnstileStatus,
  onContinue,
  onSubmit,
}: NavigationButtonsProps) {
  const { t } = useTranslation();
  const { currentStep, totalSteps, previousStep, nextStep, isSubmitting } = useDynamicForm();

  const isDynamic = (currentScreen as ScreenWithFields)?.type === 'dynamic';
  const isDynamicScreenBlocking =
    isDynamic &&
    (!dynamicFieldsCache[currentScreen?.id || ''] || prefetchingStepIndices.has(currentStep));
  const isLastStep = currentStep >= totalSteps - 1;

  return (
    <>
      {/* No top-padding here — desktop callers wrap with their own
          spacing; the mobile fixed bottom bar handles its own padding. */}
      <div className="flex items-center justify-between gap-2 sm:gap-4">
        {/* Left side - Back button */}
        {currentStep > 0 ? (
          <Button
            variant="primaryOutline"
            size="sm"
            onClick={previousStep}
            className="flex items-center gap-1.5 sm:gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('EMPLOYMENTAGREEMENT_SMARTFLOW_NAVIGATION_BACK')}
          </Button>
        ) : (
          <div />
        )}

        {/* Right side buttons */}
        <div className="flex items-center gap-3">
          {/* Dynamic screen: Skip button */}
          {isDynamic && dynamicFieldsCache[currentScreen?.id || ''] && !isLastStep && (
            <Button
              variant="ghost"
              onClick={nextStep}
              className="flex items-center gap-2 text-globe-grey hover:text-[hsl(var(--fg))]"
            >
              <SkipForward className="w-4 h-4" />
              {t('EMPLOYMENTAGREEMENT_SMARTFLOW_NAVIGATION_SKIPSTEP')}
            </Button>
          )}

          {!isLastStep ? (
            <Button
              variant="primary"
              onClick={onContinue}
              disabled={dynamicFieldsLoading || isDynamicScreenBlocking}
              className="flex items-center gap-2 sm:gap-3 px-5 py-3 sm:px-8 sm:py-4 group transition-all duration-300 hover:shadow-lg hover:shadow-[hsl(var(--brand-primary))/20]"
              size="lg"
            >
              {dynamicFieldsLoading ? (
                <>
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                  <span className="text-sm sm:text-base">
                    {t('EMPLOYMENTAGREEMENT_SMARTFLOW_STATUS_LOADING')}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-sm sm:text-base">
                    {t('EMPLOYMENTAGREEMENT_SMARTFLOW_NAVIGATION_CONTINUE')}
                  </span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </Button>
          ) : (
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                variant="primary"
                onClick={onSubmit}
                disabled={
                  isSubmitting ||
                  !turnstileToken ||
                  turnstileStatus !== 'success' ||
                  dynamicFieldsLoading ||
                  isDynamicScreenBlocking
                }
                className="flex items-center gap-2 sm:gap-3 px-5 py-3 sm:px-8 sm:py-4"
                size="lg"
              >
                {dynamicFieldsLoading || isDynamicScreenBlocking ? (
                  <>
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    <span className="text-sm sm:text-base">
                      {t('EMPLOYMENTAGREEMENT_SMARTFLOW_STATUS_LOADING')}
                    </span>
                  </>
                ) : isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    <span className="text-sm sm:text-base">
                      {t('EMPLOYMENTAGREEMENT_SMARTFLOW_STATUS_GENERATING')}
                    </span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:scale-110" />
                    <span className="text-sm sm:text-base">{t('COMMON_GENERATEDOCUMENT')}</span>
                  </>
                )}
              </Button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Hint - Hidden on touch devices */}
      {!isLastStep && (
        <p className="hidden md:block text-center mt-6 text-sm text-brand-muted">
          {t('EMPLOYMENTAGREEMENT_SMARTFLOW_HINT_PRESSENTERTOCONTINUE')}
        </p>
      )}
    </>
  );
}
